import { useMemo, useState } from 'react';
import VendorProducts from '@components/vendors/VendorProducts';
import config from '@config/index';
import CartProvider from '@context/cart-context/CartContext';
import { GoodfyndSitesBusinessInfo } from '@features/goodfynd-sites/components/goodfynd-sites-business-info';
import { GoodfyndSitesContactInfo } from '@features/goodfynd-sites/components/goodfynd-sites-contact-info';
import { GoodfyndSitesFooter } from '@features/goodfynd-sites/components/goodfynd-sites-footer';
import { GoodfyndSitesHeader } from '@features/goodfynd-sites/components/goodfynd-sites-header/GoodfyndSitesHeader';
import { useGoodfyndSite } from '@features/goodfynd-sites/context/goodfynd-site-context';
import { Container } from '@goodfynd/layout';
import { FlexView, Text } from '@goodfynd/ui';
import { VendorImage } from '@hooks/useVendorSearch/VendorImages';
import { replaceUrlParams } from '@utils/string-util';
import classnames from 'clsx';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import type { CategoryProduct } from 'types/shared';
import { ScheduleMapEmbed } from 'widgets/ScheduleMapEmbed';

import type { GoodfyndSitesPageProps } from './types';

import classes from './GoodfyndSitesPage.module.css';

export const GoodfyndSitesPage = observer(
  ({ className }: GoodfyndSitesPageProps) => {
    const router = useRouter();
    const {
      currentLocation,
      currentPickupLocation,
      isPreview,
      locations,
      orderingLocations,
      organization,
    } = useGoodfyndSite();

    const [hasProducts, setHasProducts] = useState(true);

    console.log('organization', organization);

    const defaultMenuId = useMemo(
      () =>
        currentPickupLocation?.productGroupId ||
        currentLocation?.productGroupId ||
        organization.defaultMenuId ||
        locations[0]?.productGroupId,
      [
        currentLocation?.productGroupId,
        currentPickupLocation?.productGroupId,
        locations,
        organization.defaultMenuId,
      ]
    );

    const handleProductClick = (product: CategoryProduct) => {
      router.push(
        replaceUrlParams(config.routes.goodfyndSites.products.details, {
          id: product.id,
        }),
        undefined,
        { shallow: true }
      );
    };

    const renderSection = (
      id: string,
      title: string,
      content: React.ReactNode
    ) => {
      return (
        <div className={classes['page-section']} id={id}>
          <Text align="center" as="heading3">
            {title}
          </Text>
          {content}
        </div>
      );
    };

    return (
      <CartProvider
        isGoodfyndSite
        orderingEnabled={organization.orderingEnabled && !!orderingLocations[0]}
      >
        <div className={classnames(classes.container, className)}>
          <GoodfyndSitesHeader logoImageUrl={organization.logoImageUrl} />

          <Container
            className={classnames(
              'page-container padded',
              classes.container,
              className
            )}
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              minHeight: '100vh',
              '&.MuiContainer-root': {
                pb: '48px !important',
              },
            }}
          >
            <VendorImage image={organization.heroImageUrl} />

            <FlexView align="center" direction="column" gap={32} width="100%">
              <GoodfyndSitesBusinessInfo />

              {hasProducts &&
                renderSection(
                  'menu',
                  'Our Menu',
                  <VendorProducts
                    isPreview={isPreview}
                    menuId={defaultMenuId}
                    onProductClick={handleProductClick}
                    onProductsLoaded={isPreview ? undefined : setHasProducts}
                    vendorNameId={currentPickupLocation?.vendorNameId}
                    css={{
                      alignSelf: 'flex-start',
                      '@sm': {
                        padding: '0 16px',
                      },
                    }}
                  />
                )}

              {renderSection(
                'schedule',
                'Where to Find Us',
                <div className={classes['schedule-map-container']}>
                  <ScheduleMapEmbed
                    getHref={(item) => `/schedules/${item.id}`}
                    height={750}
                    scheduleOptions={{
                      dayPickerLabel: 'Upcoming',
                      organizationId: organization.organizationId,
                      radius: 100000,
                    }}
                  />
                </div>
              )}

              {!!organization.ourStory &&
                renderSection(
                  'about-us',
                  'Our Story',
                  <div className={classes['our-story-content']}>
                    <Text align="center" tag="pre">
                      {organization.ourStory}
                    </Text>
                  </div>
                )}

              {renderSection(
                'contact-us',
                'Get in Touch',
                <GoodfyndSitesContactInfo
                  contact={organization.contact}
                  socialLinks={{
                    facebook: organization.socials.facebookUrl,
                    instagram: organization.socials.instagramUrl,
                    x: organization.socials.xUrl,
                    otherUrls: organization.socials.otherUrls,
                  }}
                />
              )}
            </FlexView>
          </Container>

          <GoodfyndSitesFooter />
        </div>
      </CartProvider>
    );
  }
);
